import { useFormContext } from "react-hook-form";
import type { ResumeData } from "@/interfaces";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utils";
import { StepKeysEnum } from "@/enums";
import { StepWrapper } from "@/components";

export function PersonalInfoStep() {
  const {
    register,
    formState: { errors },
  } = useFormContext<ResumeData>();

  const { t } = useTranslation();

  return (
    <StepWrapper>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-3">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.NAME")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="text"
              {...register("personalInfo.name")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.NAME_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.name && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.name,
                  fieldKey: "NAME",
                  minLength: 3,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-3">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.LAST_NAME")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="text"
              {...register("personalInfo.lastName")}
              className="w-full px-4 py-3 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.LAST_NAME_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.lastName && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.lastName,
                  fieldKey: "LAST_NAME",
                  minLength: 3,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-(--text-secondary) mb-3">
              {t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PROFESSIONAL_TITLE"
              )}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="text"
              {...register("personalInfo.professionalTitle")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PROFESSIONAL_TITLE_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.professionalTitle && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.professionalTitle,
                  fieldKey: "PROFESSIONAL_TITLE",
                  minLength: 3,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.EMAIL")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="email"
              {...register("personalInfo.email")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.EMAIL_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.email && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.email,
                  fieldKey: "EMAIL",
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PHONE")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="tel"
              {...register("personalInfo.phone")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PHONE_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.phone && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.phone,
                  fieldKey: "PHONE",
                  minLength: 9,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.COUNTRY")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="text"
              {...register("personalInfo.country")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.COUNTRY_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.country && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.country,
                  fieldKey: "COUNTRY",
                  minLength: 3,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.CITY")}{" "}
              <span className="text-(--primary)">*</span>
            </label>
            <input
              type="text"
              {...register("personalInfo.city")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.CITY_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.city && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.city,
                  fieldKey: "CITY",
                  minLength: 3,
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.WEBSITE")}{" "}
            </label>
            <input
              type="url"
              {...register("personalInfo.website")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.WEBSITE_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.website && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.website,
                  fieldKey: "WEBSITE",
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.LINKEDIN")}{" "}
            </label>
            <input
              type="url"
              {...register("personalInfo.linkedin")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.LINKEDIN_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.linkedin && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.linkedin,
                  fieldKey: "LINKEDIN",
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.GITHUB")}{" "}
            </label>
            <input
              type="url"
              {...register("personalInfo.github")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.GITHUB_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.github && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.github,
                  fieldKey: "GITHUB",
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-(--text-secondary) mb-2">
              {t("GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.BEHANCE")}{" "}
            </label>
            <input
              type="url"
              {...register("personalInfo.behance")}
              className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all"
              placeholder={t(
                "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.BEHANCE_PLACEHOLDER"
              )}
            />
            {errors.personalInfo?.behance && (
              <p className="mt-3 ml-1 text-sm text-(--primary)">
                {getErrorMessage({
                  t,
                  error: errors.personalInfo.behance,
                  fieldKey: "BEHANCE",
                  stepKey: StepKeysEnum.PERSONAL_INFO,
                })}
              </p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-(--text-secondary) mb-2">
            {t(
              "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PROFESSIONAL_SUMMARY"
            )}{" "}
            <span className="text-(--primary)">*</span>
          </label>
          <textarea
            {...register("personalInfo.professionalSummary")}
            rows={6}
            className="w-full px-4 py-4 bg-(--background-secondary) border border-(--border) rounded-lg text-(--text-primary) placeholder-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--primary) focus:border-transparent transition-all resize-none"
            placeholder={t(
              "GENERATE_RESUME.FORM_STEPS.PERSONAL_INFO.FIELDS.PROFESSIONAL_SUMMARY_PLACEHOLDER"
            )}
          />
          {errors.personalInfo?.professionalSummary && (
            <p className="mt-3 ml-1 text-sm text-(--primary)">
              {getErrorMessage({
                t,
                error: errors.personalInfo.professionalSummary,
                fieldKey: "PROFESSIONAL_SUMMARY",
                minLength: 50,
                stepKey: StepKeysEnum.PERSONAL_INFO,
              })}
            </p>
          )}
        </div>
      </motion.div>
    </StepWrapper>
  );
}
